// hooks/maintenance/useMaintenanceDetails.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// import { fetchMaintenanceById } from "../../api/maintenance/maintenanceService"; // si lo usas
import {
  fetchMaintenanceDetails,
  fetchMaintenanceDetailById,
  createMaintenanceDetail,
  updateMaintenanceDetail,
  deleteMaintenanceDetail,
  createMaintenanceDetailsBulk,   // ← NUEVO
} from '../../api/maintenance/maintenanceDetailsService'; // ← corrige path

/**
 * Listado de detalles por mantenimiento (agrupado por sistema).
 * Soporta:
 * - paginación OFFSET (page/limit)
 * - o keyset (cursor/limit) si pasas cursor
 */
export const useMaintenanceDetails = (
  manId,
  { page = 1, limit = 10, estreg = 'A', cursor = null, sisvehid = null } = {}
) => {
  const queryClient = useQueryClient();

  const queryKey = ['maintenanceDetails', manId, { page, limit, estreg, cursor, sisvehid }];

  const {
    data = cursor
      ? { grupos: [], cursor: null, nextCursor: null, hasMore: false, total: null }
      : { grupos: [], page, limit, total: 0 },
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => fetchMaintenanceDetails({ manId, page, limit, estreg, cursor, sisvehid }),
    enabled: Number.isInteger(manId) && manId > 0,
    keepPreviousData: true,
    staleTime: 1000 * 60 * 2,
  });

  // Conveniencia: groups e items planos derivados
  const groups = data.grupos ?? [];
  const itemsFlat = groups.flatMap(g => g.items ?? []);

  const invalidateList = () => {
    queryClient.invalidateQueries({ queryKey: ['maintenanceDetails', manId] });
  };

  const createMutation = useMutation({
    mutationFn: ({ payload }) => createMaintenanceDetail({ manId, payload }),
    onSuccess: () => {
      invalidateList();
    },
  });

  const bulkCreateMutation = useMutation({
    mutationFn: ({ comids, defaults, crebyid }) =>
      createMaintenanceDetailsBulk({ manId, comids, defaults, crebyid }),
    onSuccess: () => {
      invalidateList();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ detailId, payload }) => updateMaintenanceDetail({ manId, detailId, payload }),
    onSuccess: (updated) => {
      invalidateList();
      // cache puntual opcional
      queryClient.setQueryData(['maintenanceDetail', manId, updated.mandetid ?? updated.manDetId], updated);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ detailId, updbyid }) => deleteMaintenanceDetail({ manId, detailId, updbyid }),
    onSuccess: () => {
      invalidateList();
    },
  });

  return {
    data,            // respuesta cruda (con page/limit o cursor/nextCursor)
    groups,          // [{ sisvehid, sistemanombre, count, items: [] }]
    items: itemsFlat,// lista plana derivada (por si algo del UI aún la usa)
    isLoading,
    isFetching,
    error,
    createMutation,
    bulkCreateMutation,
    updateMutation,
    deleteMutation,
  };
};

/** Detalle puntual por id */
export const useMaintenanceDetailById = (manId, detailId) => {
  return useQuery({
    queryKey: ['maintenanceDetail', manId, detailId],
    queryFn: () => fetchMaintenanceDetailById({ manId, detailId }),
    enabled: Number.isInteger(manId) && manId > 0 && Number.isInteger(detailId) && detailId > 0,
    staleTime: 1000 * 60 * 5,
  });
};
